/**
 * Per `voice-experiment-framework` change (voice-loop iter 1) — fan
 * SPA helper for reading the operator's voice-experiment kill-switch
 * flags. Default-on: if the table doesn't have a row for a key, every
 * consumer treats it as enabled. The operator only writes a row to
 * DISABLE an experiment.
 *
 * Loaded once at app mount (fire-and-forget; if it fails every flag
 * defaults to enabled, matching the conservative default-on stance).
 * Re-fetched every 5 minutes so a mid-session operator toggle takes
 * effect without a hard refresh.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { listVoiceFlags } from '../api/fan-api';

interface VoiceFlagsContextValue {
  /** undefined while still loading the first response. */
  flags: Record<string, boolean> | undefined;
}

const VoiceFlagsContext = createContext<VoiceFlagsContextValue>({ flags: undefined });

export function VoiceFlagsProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<Record<string, boolean> | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    let timer: number | undefined;
    const load = async (): Promise<void> => {
      try {
        const list = await listVoiceFlags();
        if (!alive) return;
        const map: Record<string, boolean> = {};
        for (const f of list) map[f.flagKey] = f.enabled;
        setFlags(map);
      } catch {
        // Network blip — leave whatever we had (or undefined on
        // first try, which means consumers fall back to defaultValue).
      }
      if (alive) timer = window.setTimeout(() => { void load(); }, 5 * 60 * 1000);
    };
    void load();
    return () => { alive = false; if (timer) window.clearTimeout(timer); };
  }, []);

  return <VoiceFlagsContext.Provider value={{ flags }}>{children}</VoiceFlagsContext.Provider>;
}

/**
 * Read a flag. `defaultValue` is what the caller WANTS when the row
 * doesn't exist; conventionally `true` so a brand-new experiment
 * works the moment its UI ships, even before the operator opens the
 * admin page.
 */
export function useVoiceFlag(flagKey: string, defaultValue: boolean): boolean {
  const ctx = useContext(VoiceFlagsContext);
  if (!ctx.flags) return defaultValue;
  return ctx.flags[flagKey] ?? defaultValue;
}
